// components/BootShield.tsx
// The presentational shield: an opaque, high-z-index overlay that covers the
// app while it boots and lifts once (with a fade) when boot completes. The
// gate cascade in App.tsx runs unchanged underneath — its intermediate screen
// swaps happen invisibly behind this layer. Mounted as a sibling of <App/>
// inside the Auth + AppData providers (see index.tsx).
import React, { useEffect, useRef, useState } from 'react';
import { useBootState } from '../hooks/useBootState';
import { BootSplash } from './BootSplash';

const FADE_MS = 280;        // splash fade-out duration
const MIN_VISIBLE_MS = 450; // floor so very fast boots still read as a deliberate splash
const HARD_CAP_MS = 12000;  // backstop: never trap the app behind the shield

type Phase = 'visible' | 'fading' | 'gone';

export const BootShield: React.FC = () => {
  const boot = useBootState();
  const booting = boot.state === 'booting';
  const [phase, setPhase] = useState<Phase>('visible');
  const [forceLift, setForceLift] = useState(false);
  const mountedAt = useRef<number>(typeof performance !== 'undefined' ? performance.now() : Date.now());
  // Adopt the pre-bundle static splash from index.html instead of mounting a
  // twin <BootSplash/>: the twin restarted every enter animation at the
  // React handoff (the visible "splash reloads" stutter). When adopted, the
  // shield renders nothing and drives the static node's fade/removal itself.
  const staticNodeRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? document.getElementById('boot-splash-static') : null
  );

  // Hard backstop — force-lift even if the boot state never settles
  useEffect(() => {
    const t = setTimeout(() => setForceLift(true), HARD_CAP_MS);
    return () => clearTimeout(t);
  }, []);

  // visible → fading once boot is done (or backstop fired), honoring the min floor
  useEffect(() => {
    if (phase !== 'visible') return;
    if (booting && !forceLift) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const wait = Math.max(0, MIN_VISIBLE_MS - (now - mountedAt.current));
    const t = setTimeout(() => setPhase('fading'), wait);
    return () => clearTimeout(t);
  }, [phase, booting, forceLift]);

  // fading → gone after the fade completes (then unmounts, freeing pointer events)
  useEffect(() => {
    if (phase !== 'fading') return;
    const t = setTimeout(() => setPhase('gone'), FADE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Drive the adopted static node through the same phase machine
  useEffect(() => {
    const node = staticNodeRef.current;
    if (!node) return;
    if (phase === 'fading') {
      node.style.transition = `opacity ${FADE_MS}ms ease`;
      node.style.opacity = '0';
      node.style.pointerEvents = 'none';
    } else if (phase === 'gone') {
      node.remove();
      staticNodeRef.current = null;
    }
  }, [phase]);

  if (phase === 'gone') return null;
  // Static splash adopted — it IS the shield; render no twin.
  if (staticNodeRef.current) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        opacity: phase === 'fading' ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        pointerEvents: phase === 'fading' ? 'none' : 'auto',
      }}
    >
      <BootSplash />
    </div>
  );
};
